import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { UserContextService } from '../services/user-context.service';
import { ACCOUNT_ONLY_APP_PATHS } from './account-only-app-paths';

function firstAppSegment(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  const parts = path.replace(/^\/app\/?/, '').split('/').filter(Boolean);
  return parts[0] ?? '';
}

/** Redirect `/app` to dashboard when the user has an employee record, otherwise to profile. */
export const appHomeRedirectGuard: CanActivateFn = async () => {
  const userContext = inject(UserContextService);
  const router = inject(Router);
  await userContext.ensureInitialized();
  return userContext.hasOrganizationAccess()
    ? router.createUrlTree(['/app/dashboard'])
    : router.createUrlTree(['/app/profile']);
};

/**
 * Require a linked employee document for all app areas except account-only routes.
 * Users who still have Auth + user profile but no employee see only account screens.
 */
export const organizationEmployeeChildGuard: CanActivateChildFn = async (
  _childRoute,
  state
) => {
  const router = inject(Router);
  const userContext = inject(UserContextService);
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const translate = inject(TranslateService);

  await userContext.ensureInitialized();
  const raw = (userContext.role() || '').toLowerCase();
  if (raw === 'agent' || raw === 'maintenance') {
    const key = 'auth.agent-use-mobile-app';
    let msg = translate.instant(key);
    if (!msg || msg === key) {
      msg =
        'Please use the Servvis Agent mobile app. If you don\'t have access, ask your manager to send you an invitation.';
    }
    await authService.signOut();
    toastService.showError(msg);
    return router.createUrlTree(['/index']);
  }

  if (ACCOUNT_ONLY_APP_PATHS.has(firstAppSegment(state.url))) {
    return true;
  }
  if (!userContext.hasOrganizationAccess()) {
    return router.createUrlTree(['/app/profile']);
  }
  return true;
};
